"use client";

import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { PropsWithChildren, useState } from "react";
import { parseApiError } from "@/lib/errorUtils";

// TanStack Query v5 retries a failed query 3 times by default. A 4xx is the
// server answering definitively — a 404 for an optional resource the user has
// never created, a 403, a rejected payload — so retrying only multiplies the
// request (and the proxy's log record) by four without ever changing the
// answer. Retry 5xx, timeouts and transport failures; never a client error.
const MAX_QUERY_RETRIES = 3;

const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  const { statusCode } = parseApiError(error);
  if (statusCode >= 400 && statusCode < 500) {
    return false;
  }
  return failureCount < MAX_QUERY_RETRIES;
};

const ReactQueryProvider = ({ children }: PropsWithChildren) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: shouldRetryQuery },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};

export default ReactQueryProvider;
